import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MediaPicker } from "@/components/admin/MediaPicker";

export default function AdminSEO() {
  const [seo, setSeo] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const r = await api.get("/admin/content/seo");
      setSeo(r.data.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (!seo) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>;
  }

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSeo((s) => ({ ...s, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/content/seo", { data: seo });
      toast.success("SEO settings saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-seo">
      <h1 className="font-display text-2xl font-black uppercase text-zinc-900 mb-1">SEO</h1>
      <p className="text-sm text-zinc-500 mb-6">Global search and social sharing settings. Vehicles and pages support individual SEO overrides.</p>

      <form onSubmit={save} className="bg-white border border-zinc-200 p-6 space-y-5 max-w-2xl">
        <div>
          <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Site Title</Label>
          <Input value={seo.site_title || ""} onChange={set("site_title")} className="bg-white border-zinc-300 text-zinc-800 rounded-sm" data-testid="seo-site-title" />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Default Meta Description</Label>
          <Textarea value={seo.meta_description || ""} onChange={set("meta_description")} className="bg-white border-zinc-300 text-zinc-800 rounded-sm min-h-[80px]" data-testid="seo-meta-description" />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Social Sharing Image</Label>
          <MediaPicker value={seo.social_image} onChange={(v) => setSeo((s) => ({ ...s, social_image: v }))} testid="seo-social-image" />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
          <input type="checkbox" checked={seo.allow_indexing !== false} onChange={set("allow_indexing")} className="accent-[#FF6B00] h-4 w-4" data-testid="seo-allow-indexing" />
          Allow search engine indexing
        </label>
        <Button type="submit" disabled={saving} className="bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm" data-testid="seo-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save SEO Settings"}
        </Button>
      </form>
    </div>
  );
}
